import { memoizedGroupBySelector } from '@travelnest/entities';
import { INavButtonItem, INavLinkItem, ProductFrame as PF } from '@travelnest/hui';
import { useDataLoad } from '@travelnest/merlin-hooks';
import React from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { toggleFreshChatWidget } from '../freshchat/FreshChatWidget';

import { customerIsLegacyCommission } from '../../../data/entities/commission/selectors';
import { IPropertySummary } from '../../../data/entities/property-summary';
import { actions } from '../../../data/store';
import { isAdmin as selectIsAdmin } from '../../../entities/admin/selectors';
import { destroyCurrentSession } from '../../../entities/auth/session/actions';
import { currentUserId } from '../../../entities/auth/session/selectors';
import { setRedirectUrl } from '../../../entities/redirect/actions';
import { useCustomerExperiments } from '../../../experiment';
import { IThunkDispatch } from '../../../store/middleware';
import { IState } from '../../../store/reducer';
import { customerIsIncomplete, hasOnlyOneUnpublishedProperty } from '../../helpers/customer';
import { Tracking } from '../tracking/TrackingContainer';
import { getMenuItems, getNavigationItems, getSubNavigationItems } from './getNavItems';

interface INavItems {
	menu: Array<(INavLinkItem | INavButtonItem)>;
	navigation: Array<(INavLinkItem | INavButtonItem)>;
	subnavigation: Array<(INavLinkItem | INavButtonItem)>;
}

type NavBuilder = (
	navigate: NavigateFunction,
	logout: () => void,
	path: string,
	customerId: string,
	isLegacyCommission: boolean | undefined,
	onboardingComplete: boolean,
) => INavItems;

const getNavItems: NavBuilder = (navigate, logout, path, customerId, isLegacyCommission, onboardingIncomplete) => {
	const isAdmin = useSelector<IState, boolean>(selectIsAdmin);

	const menuItems = getMenuItems(navigate, path, onboardingIncomplete, toggleFreshChatWidget, logout);
	const navigationItems = getNavigationItems(navigate, path, onboardingIncomplete, isLegacyCommission);
	const subnavigationItems = getSubNavigationItems(navigate, path, onboardingIncomplete, isAdmin, toggleFreshChatWidget, logout);

	return {
		menu: menuItems,
		navigation: navigationItems,
		subnavigation: subnavigationItems,
	};
};

export const ProductFrame: React.FC<React.PropsWithChildren> = ({ children }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch<IThunkDispatch>();
	const customerId = useSelector<IState, string>(currentUserId);
	const properties = useSelector<IState, IPropertySummary[]>(
		(state) => (memoizedGroupBySelector('owner_id', state.data?.propertySummaries?.byId)[customerId] || []).filter((p) => p.is_active)
	);

	// Task-Based Property and Property Portal track onboarding differently
	let onboardingIncomplete;
	const customerExperiments = useCustomerExperiments(customerId);
	if (customerExperiments?.property_portal === 'on') {
		onboardingIncomplete = hasOnlyOneUnpublishedProperty(properties);
	} else {
		onboardingIncomplete = customerIsIncomplete(properties);
	}

	const isLegacyCommission = useSelector<IState, boolean | undefined>((state) => customerIsLegacyCommission(state, customerId));
	useDataLoad(isLegacyCommission === undefined, () => dispatch(actions.commission.getByCustomer(customerId)));

	const logout = () => {
		dispatch(destroyCurrentSession());
		dispatch(setRedirectUrl('/'));
		navigate('/');
	};

	return <PF {...getNavItems(navigate, logout, location.pathname, customerId, isLegacyCommission, onboardingIncomplete)}>{children}</PF>;
};

type PropsOf<T> = T extends React.ComponentType<React.PropsWithChildren<infer P>> ? P : never;

export const withProductFrame = <T extends React.ComponentType<React.PropsWithChildren<any>>, _>(WrappedComponent: T) => {
	return (props: PropsOf<T>) => <ProductFrame>
		<Tracking />
		<WrappedComponent {...props} />
	</ProductFrame>;
};
